import React, { useState } from 'react';
import {
  Modal,
  Button,
  Upload,
  message,
  Slider,
  Row,
  Col,
  Tabs,
  Input,
  Form,
  Card,
  Divider,
} from 'antd';
import PropTypes from 'prop-types';

import { useMutation, useReactiveVar, useLazyQuery } from '@apollo/client';
import { userVar } from 'graphql/cache';
import { ContentLibrary } from 'pages/Proposal/components/RichEditor/RichEditorModals/GalleryEditModal/components';
import { ADD_LIBRARY_COVER, ADD_LIBRARY_MEDIA } from 'graphql/mutations/contentLibraryMutations';
import { GENERATE_AI_IMAGES, UPLOAD_AI_IMAGE } from 'graphql/queries/proposalQueries';
import { resizeImageForS3 } from 'utils/uploadToS3';
import { ArrowTop, DefaultImageIcon, CloseIcon, AIIcon } from 'components/Icons';

import Popconfirm from 'components/PopConfirm';
import Uploader from 'components/Uploader';
import UploadCloudIcon from 'components/Icons/UploadCloudIcon';

import './UploadContentLibraryItem.scss';

/**
 * GraphQL returns strings (data URLs or http(s)). Normalizes legacy raw base64 or Buffer-shaped JSON.
 * @param {unknown} value - Item from generateAIImages
 * @returns {string} Displayable image URL
 */
const normalizeAIImageUrl = (value) => {
  if (value == null || value === '') return '';

  if (typeof value === 'object' && value.type === 'Buffer' && Array.isArray(value.data)) {
    const u8 = new Uint8Array(value.data);
    let binary = '';
    const chunkSize = 0x8000;

    for (let i = 0; i < u8.length; i += chunkSize) {
      binary += String.fromCharCode.apply(null, u8.subarray(i, i + chunkSize));
    }

    return `data:image/png;base64,${btoa(binary)}`;
  }

  if (typeof value !== 'string') return String(value);

  const s = value.trim();

  if (s.startsWith('data:') || /^https?:\/\//i.test(s)) return s;

  if (/^[A-Za-z0-9+/=\s]+$/.test(s) && s.length > 32) {
    return `data:image/png;base64,${s.replace(/\s/g, '')}`;
  }

  return s;
};

const { Dragger } = Upload;
const { TextArea } = Input;
const { TabPane } = Tabs;

/**
 * Upload content library item
 *
 * @param {Object} props
 * @param {Function} props.cancelUploadModal
 * @param {string} props.selectedCoverType
 * @param {boolean} props.showUploadModal
 * @param {string} props.selectedFolderKey
 * @param {Function} props.onSuccess
 * @param {string} props.type
 * @param {Object} props.prop - Proposal object (for gallery/image-editor types)
 */
const UploadContentLibraryItem = ({
  cancelUploadModal,
  selectedCoverType,
  showUploadModal,
  selectedFolderKey,
  onSuccess,
  type,
  multiSelect,
  prop,
  onAddToGallery,
  isWix,
  imageSelectionModalType,
}) => {
  const [fileList, setFileList] = useState([]);
  const [upload, uploadStatus] = Uploader();
  const [uploadingFileName, setUploadingFileName] = useState('');
  const [selectedItems, setSeletedItem] = useState({});
  const [showBack, setShowBack] = useState(false);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [aiImageResults, setAiImageResults] = useState([]);
  const [selectedAIImages, setSelectedAIImages] = useState([]);
  const [activeTab, setActiveTab] = useState(imageSelectionModalType || 'upload');
  const [urlInput, setUrlInput] = useState('');
  const [imageUrlError, setImageUrlError] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [uploadedImagesUrls, setUploadedImagesUrls] = useState([]);
  const [urlForm] = Form.useForm();
  const [aiForm] = Form.useForm();

  const user = useReactiveVar(userVar);

  const [addContentCover] = useMutation(ADD_LIBRARY_COVER);
  const [addContentMedia] = useMutation(ADD_LIBRARY_MEDIA);
  const [uploadAIImage, { loading: uploadingAIImage }] = useLazyQuery(UPLOAD_AI_IMAGE);

  // Allow any image URL (just check for http/https prefix, leave extension/type checks to backend or actual upload)
  const imageUrlRegex = /^https?:\/\/.+/i;

  /**
   * Generate AI images
   * @returns {void}
   */
  const [generateImage, { loading: runningImagePrompt }] = useLazyQuery(GENERATE_AI_IMAGES, {
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    onCompleted: (data) => {
      const { generateAIImages } = data;

      if (!generateAIImages || generateAIImages.length === 0) {
        message.error('No images were generated. Please try again with a different prompt.');
        return;
      }

      const timestamp = Date.now();
      const normalizedUrls = generateAIImages
        .map((item) => normalizeAIImageUrl(item))
        .filter(Boolean);
      if (normalizedUrls.length === 0) {
        message.error('No images were generated. Please try again with a different prompt.');
        return;
      }

      const imageResults = normalizedUrls.map((url, index) => ({
        url,
        name: `ai-${timestamp}-${index}.png`,
      }));

      setAiImageResults(imageResults);

      // Automatically select all generated images
      const aiImageFiles = imageResults.map((imageData) => {
        const imageUrl = typeof imageData === 'string' ? imageData : imageData.url;
        const imageName = typeof imageData === 'string' ? `ai-${timestamp}.png` : imageData.name;

        return {
          uid: `ai-${timestamp}-${Math.random()}`,
          name: imageName,
          status: 'done',
          url: imageUrl,
          originFileObj: null,
          isAIGenerated: true,
        };
      });

      setSelectedAIImages((prevSelected) => {
        const existingUrls = new Set(prevSelected.map((img) => img.url));
        const newImages = aiImageFiles.filter((img) => !existingUrls.has(img.url));
        if (!multiSelect && Array.isArray(newImages) && newImages.length > 0) {
          return [newImages[newImages.length - 1]];
        }
        return [...prevSelected, ...newImages];
      });
    },
    onError: (error) => {
      let errorMessage = 'Failed to generate images. Please try again.';
      if (error?.graphQLErrors?.[0]?.message) {
        errorMessage = error.graphQLErrors[0].message;
      } else if (error?.networkError?.result?.errors?.[0]?.message) {
        errorMessage = error.networkError.result.errors[0].message;
      } else if (error?.message) {
        errorMessage = error.message;
      }

      message.error(errorMessage);
    },
  });

  /**
   * On cancel modal
   */
  const onCancelModal = () => {
    setFileList([]);
    cancelUploadModal();
    setSeletedItem({});
    setActiveTab('upload');
    setUrlInput('');
    setAiPrompt('');
    setAiImageResults([]);
    setSelectedAIImages([]);
    setUploadedImagesUrls([]);
    setUploadingFileName('');
    urlForm.resetFields();
    aiForm.resetFields();
  };

  /**
   * Handle the close modal
   * @returns {void}
   */
  const handleCloseModal = () => {
    setShowCloseConfirm(true);
  };

  /**
   * Handle the cancellation of the close modal
   * @returns {void}
   */
  const handleCancelClose = () => {
    setShowCloseConfirm(false);
  };

  /**
   * Handle the confirmation of the close modal
   * @returns {void}
   */
  const handleConfirmClose = () => {
    setShowCloseConfirm(false);
    onCancelModal();
  };

  /**
   * Upload files to the content library
   * @param {Array} files - The files to upload
   * @returns {Promise} - Promise that resolves when the upload is complete
   */
  const onUpload = async (files) => {
    const size =
      selectedCoverType === 'full-page'
        ? { width: 900, height: 1250 }
        : type === 'gallery' || type === 'image-editor'
        ? { width: 950, height: 2000 }
        : type === 'attachments'
        ? { width: 1200, height: 2000 }
        : { width: 900, height: 675 };

    let filteredAndResizedFiles = [];

    try {
      if (type === 'attachments') {
        const processedFiles = await Promise.all(
          files.flatMap(async (file) => {
            if (file.type && file.type.includes('image')) {
              const uploadPath = 'attachments';
              return await resizeImageForS3({ file, size, path: uploadPath });
            } else {
              return file;
            }
          })
        );
        filteredAndResizedFiles = [...filteredAndResizedFiles, ...processedFiles];
      } else {
        const resizedFiles = await Promise.all(
          files.flatMap(async (file) => {
            const uploadPath = type === 'gallery' || type === 'image-editor' ? 'sections' : type;
            return await resizeImageForS3({ file, size, path: uploadPath });
          })
        );
        filteredAndResizedFiles = [...filteredAndResizedFiles, ...resizedFiles];
      }
    } catch (e) {
      return message.error(e.message);
    }

    if (!filteredAndResizedFiles.length) return;

    const uploadPath =
      type === 'gallery' || type === 'image-editor'
        ? `props/${prop?._id || user._id}/section`
        : type === 'attachments'
        ? `props/${prop?._id || user._id}/attachments`
        : `${type}/${user._id}`;

    const urls = await upload(filteredAndResizedFiles, uploadPath);
    let products = [];

    await Promise.all(
      urls.map(async ({ url, filename, filetype }, index) => {
        if (type === 'attachments') {
        } else if (type === 'covers') {
          const cover = {
            img: url,
            alt: url.substring(url.lastIndexOf('/') + 1),
            uploadedBy: 'user',
            uid: user._id,
            type: selectedCoverType === 'full-page' ? 'full-page' : 'top',
            createdAt: new Date(),
          };

          if (user && user.teamId) {
            cover.auid = user.teamId;
          }

          await addContentCover({
            variables: {
              cover,
              folderKey: selectedFolderKey,
            },
          });

          message.success(
            `${
              selectedCoverType === 'full-page' ? 'FULL PAGE COVER' : 'TOP COVER'
            } cover uploaded successfully`
          );
        } else if (type === 'media') {
          const media = {
            src: url,
            uploadedBy: 'user',
            uid: user._id,
            type: filetype,
            name: filename,
            createdAt: new Date(),
          };

          if (user && user.teamId) {
            media.auid = user.teamId;
          }

          await addContentMedia({
            variables: {
              media,
              folderKey: selectedFolderKey,
            },
          });

          message.success(`Media uploaded successfully`);
        } else if (type === 'catalogs') {
          products.push({
            url: url,
            name: filename,
            type: filetype,
          });
        } else if (type === 'gallery' || type === 'image-editor') {
          // For gallery and image-editor, just return URLs without saving to library
          // No action needed here, URLs will be returned in onSuccess
        }
      })
    );

    if (type === 'catalogs') {
      onSuccess(products);
      onCancelModal();
    } else if (type === 'attachments') {
      const attachmentFiles = urls.map(({ url, filename, filetype }, index) => ({
        url,
        type: filetype || files[index]?.type || 'file',
        name: filename || files[index]?.name,
        size: files[index]?.size || 0,
      }));
      if (onSuccess) {
        onSuccess(attachmentFiles);
      }
      onCancelModal();
    } else if (type === 'gallery' || type === 'image-editor') {
      if (onSuccess) {
        onSuccess(urls, activeTab);
      }

      const imageUrls = urls.map((item) => item.url);
      setUploadedImagesUrls((prev) => [...prev, ...imageUrls]);
      setFileList([]);
      setActiveTab('upload');
      setUploadingFileName('');
    } else if (onSuccess) {
      onSuccess(urls);
      onCancelModal();
      setUploadingFileName('');
    }
  };

  /**
   * Handle upload
   * @returns {Promise} - Promise that resolves when the upload is complete
   */
  const handleUpload = async () => {
    if (
      type === 'gallery' &&
      !(activeTab === 'upload' && fileList && fileList.length > 0) &&
      !(activeTab === 'library' && Object.keys(selectedItems).length > 0) &&
      !(activeTab === 'url' && urlInput && urlInput.trim()) &&
      !((activeTab === 'ai' || activeTab === 'regenerate') && selectedAIImages.length > 0)
    ) {
      if (onAddToGallery) {
        onAddToGallery();
      } else {
        setUploadedImagesUrls([]);
        onCancelModal();
      }
      return;
    }

    if (activeTab === 'upload' && fileList && fileList.length > 0) {
      await onUpload(fileList);
    } else if (activeTab === 'library' && Object.keys(selectedItems).length > 0) {
      if (type === 'gallery' || type === 'image-editor') {
        const items = Object.values(selectedItems).map((item) => ({
          url: item.src,
          src: item.src,
          filename: item.name,
          name: item.name,
          width: item.width,
          height: item.height,
        }));

        if (onSuccess) {
          onSuccess(items, activeTab);
        }
        setSeletedItem({});
      } else if (type === 'attachments') {
        const items = Object.values(selectedItems).map((item) => ({
          url: item.src,
          type: item.type || 'file',
          name: item.name,
          size: item.size || 0,
        }));

        if (onSuccess) {
          onSuccess(items);
        }
        onCancelModal();
        setSeletedItem({});
      } else if (type !== 'catalogs' && type !== 'pricing') {
        const [selectedItem] = Object.values(selectedItems);

        if (onSuccess) {
          onSuccess([{ url: selectedItem.src, filename: selectedItem.name }]);
        }

        onCancelModal();
        setSeletedItem({});
      } else if (type === 'catalogs' || type === 'pricing') {
        const products = Object.values(selectedItems).map((item) => ({
          url: item.src,
          name: item.name,
          type: item.type,
        }));

        if (onSuccess) {
          onSuccess(products);
        }

        onCancelModal();
        setSeletedItem({});
      }
    } else if (activeTab === 'url') {
      if (!imageUrlRegex.test(urlInput)) {
        message.error('Please enter a valid image URL');
        return;
      }
      await handleUrlUpload();
    } else if ((activeTab === 'ai' || activeTab === 'regenerate') && selectedAIImages.length > 0) {
      await handleAIImagesUpload();
    }
  };

  /**
   * Upload image from URL
   * @returns {Promise} - Promise that resolves when the upload is complete
   */
  const handleUrlUpload = async () => {
    if (!urlInput || !urlInput.trim()) {
      message.error('Please enter a valid image URL');
      return;
    }

    const fileName = urlInput.substring(urlInput.lastIndexOf('/') + 1) || 'image.png';
    setUploadingFileName(fileName);

    try {
      message.loading('Uploading image from URL...');

      const filePath =
        type === 'gallery' || type === 'image-editor'
          ? `props/${prop?._id || user._id}/section`
          : `${type}/${user._id}`;

      const { data } = await uploadAIImage({
        variables: {
          imageUrl: urlInput.trim(),
          filePath: filePath,
        },
      });

      const uploadedUrl = data?.uploadAIImage;

      if (!uploadedUrl) {
        message.destroy();
        setUploadingFileName('');
        message.error('Failed to upload image from URL. Please try again.');
        return;
      }

      if (type === 'gallery' || type === 'image-editor') {
        if (onSuccess) {
          onSuccess([{ url: uploadedUrl }], activeTab);
        }
        setUploadedImagesUrls((prev) => [...prev, uploadedUrl]);
        message.destroy();
        setUrlInput('');
        urlForm.resetFields();
        return;
      } else if (type === 'attachments') {
        if (onSuccess) {
          onSuccess([{ url: uploadedUrl, type: 'image/png', name: 'image.png', size: 0 }]);
        }
        message.destroy();
        onCancelModal();
        return;
      } else if (type === 'covers') {
        const cover = {
          img: uploadedUrl,
          alt: urlInput.substring(urlInput.lastIndexOf('/') + 1),
          uploadedBy: 'user',
          uid: user._id,
          type: selectedCoverType === 'full-page' ? 'full-page' : 'top',
          createdAt: new Date(),
        };

        if (user && user.teamId) {
          cover.auid = user.teamId;
        }

        await addContentCover({
          variables: {
            cover,
            folderKey: selectedFolderKey,
          },
        });

        message.success(
          `${
            selectedCoverType === 'full-page' ? 'FULL PAGE COVER' : 'TOP COVER'
          } cover uploaded successfully`
        );
      } else if (type === 'media') {
        const media = {
          src: uploadedUrl,
          uploadedBy: 'user',
          uid: user._id,
          type: 'image/png',
          name: urlInput.substring(urlInput.lastIndexOf('/') + 1),
          createdAt: new Date(),
        };

        if (user && user.teamId) {
          media.auid = user.teamId;
        }

        await addContentMedia({
          variables: {
            media,
            folderKey: selectedFolderKey,
          },
        });

        message.success('Media uploaded successfully');
      } else if (type === 'catalogs') {
        const products = [
          {
            url: uploadedUrl,
            name: urlInput.substring(urlInput.lastIndexOf('/') + 1),
            type: 'image/png',
          },
        ];

        if (onSuccess) {
          onSuccess(products);
        }
        message.destroy();
        onCancelModal();
        return;
      }

      message.destroy();
      if (onSuccess) {
        onSuccess([{ url: uploadedUrl }]);
      }
      setUploadingFileName('');
      onCancelModal();
    } catch (error) {
      message.destroy();
      setUploadingFileName('');
      let errorMessage = 'Failed to upload image from URL. Please try again.';
      if (error?.graphQLErrors?.[0]?.message) {
        errorMessage = error.graphQLErrors[0].message;
      } else if (error?.networkError?.result?.errors?.[0]?.message) {
        errorMessage = error.networkError.result.errors[0].message;
      } else if (error?.message) {
        errorMessage = error.message;
      }
      message.error(errorMessage);
    }
  };

  /**
   * Run the create image prompt
   * @returns {void}
   */
  const runCreateImage = () => {
    if (!aiPrompt || !aiPrompt.trim()) {
      message.error('Please enter a prompt');
      return;
    }

    const variables = { prompt: aiPrompt.trim(), max: 1 };

    // specify the image type to get the correct dimensions
    if (type === 'covers') {
      variables.imageType = `cover - ${selectedCoverType}`;
    }

    generateImage({ variables });
  };

  /**
   * Handle the selection of an AI image
   * @param {Object} imageData - The image data
   * @returns {void}
   */
  const handleAIImageSelection = (imageData) => {
    const rawUrl = typeof imageData === 'string' ? imageData : imageData.url;
    const imageUrl = normalizeAIImageUrl(rawUrl);
    const imageName = typeof imageData === 'string' ? `ai-${Date.now()}.png` : imageData.name;

    const aiImageFile = {
      uid: `ai-${Date.now()}-${Math.random()}`,
      name: imageName,
      status: 'done',
      url: imageUrl,
      originFileObj: null,
      isAIGenerated: true,
    };

    if (!selectedAIImages.find((img) => normalizeAIImageUrl(img.url) === imageUrl)) {
      setSelectedAIImages([...selectedAIImages, aiImageFile]);
    }
  };

  /**
   * Handle the removal of an AI image
   * @param {Object} imageData - The image data
   * @returns {void}
   */
  const handleAIImageRemove = (imageData) => {
    const rawUrl = typeof imageData === 'string' ? imageData : imageData.url;
    const imageUrl = normalizeAIImageUrl(rawUrl);
    setSelectedAIImages(
      selectedAIImages.filter((img) => normalizeAIImageUrl(img.url) !== imageUrl)
    );
  };

  /**
   * Upload AI images to the content library
   * @returns {Promise} - Promise that resolves when the upload is complete
   */
  const handleAIImagesUpload = async () => {
    if (selectedAIImages.length === 0) {
      message.warning('No images selected for upload.');
      return;
    }

    if (selectedAIImages.length > 0) {
      setUploadingFileName(selectedAIImages[0]?.name || 'AI Generated Image');
    }

    try {
      message.loading(
        `Uploading AI  ${
          type === 'pricing' || type === 'gallery'
            ? 'Gallery'
            : type === 'image-editor'
            ? 'Image'
            : type === 'catalogs'
            ? 'Catalog'
            : type === 'media'
            ? 'Media'
            : type === 'covers'
            ? 'Cover'
            : type === 'attachments'
            ? 'File'
            : 'Content Library'
        }...`,
        0
      );

      const filePath =
        type === 'gallery' || type === 'image-editor'
          ? `props/${prop?._id || user._id}/section`
          : `${type}/${user._id}`;

      const uploadPromises = selectedAIImages.map(async (aiImage, index) => {
        try {
          const imageUrlForUpload = normalizeAIImageUrl(aiImage.url);
          const { data } = await uploadAIImage({
            variables: {
              imageUrl: imageUrlForUpload,
              filePath: filePath,
            },
          });

          const uploadedUrl = data.uploadAIImage;

          if (type === 'gallery' || type === 'image-editor') {
            return { success: true, index, url: uploadedUrl };
          } else if (type === 'covers') {
            const cover = {
              img: uploadedUrl,
              alt: aiImage.name,
              uploadedBy: 'user',
              uid: user._id,
              type: selectedCoverType === 'full-page' ? 'full-page' : 'top',
              createdAt: new Date(),
            };

            if (user && user.teamId) {
              cover.auid = user.teamId;
            }

            await addContentCover({
              variables: {
                cover,
                folderKey: selectedFolderKey,
              },
            });
          } else if (type === 'media') {
            const media = {
              src: uploadedUrl,
              uploadedBy: 'user',
              uid: user._id,
              type: 'image/png',
              name: aiImage.name,
              createdAt: new Date(),
            };

            if (user && user.teamId) {
              media.auid = user.teamId;
            }

            await addContentMedia({
              variables: {
                media,
                folderKey: selectedFolderKey,
              },
            });
          }

          return { success: true, index, url: uploadedUrl };
        } catch (error) {
          return { success: false, index, error: error.message };
        }
      });

      const results = await Promise.all(uploadPromises);

      const successfulUploads = results.filter((r) => r.success);
      const failedUploads = results.filter((r) => !r.success);

      message.destroy();

      if (successfulUploads.length > 0) {
        if (type === 'gallery' || type === 'image-editor') {
          message.success(
            `Successfully uploaded ${successfulUploads.length} AI image${
              successfulUploads.length > 1 ? 's' : ''
            }!`
          );
        } else {
          message.success(
            `Successfully uploaded ${successfulUploads.length} AI image${
              successfulUploads.length > 1 ? 's' : ''
            } to content library!`
          );
        }
      }

      if (failedUploads.length > 0) {
        message.error(
          `Failed to upload ${failedUploads.length} image${
            failedUploads.length > 1 ? 's' : ''
          }. Please try again.`
        );
      }

      setSelectedAIImages([]);
      setAiImageResults([]);

      if (onSuccess && successfulUploads.length > 0) {
        onSuccess(
          successfulUploads.map((r) => ({ url: r.url })),
          activeTab
        );
        if (type === 'gallery' || type === 'image-editor') {
          const imageUrls = successfulUploads.map((r) => r.url);
          setUploadedImagesUrls((prev) => [...prev, ...imageUrls]);
        }
      }

      if (type === 'gallery' || type === 'image-editor') {
        setAiPrompt('');
        aiForm.resetFields();
        setUploadingFileName('');
        return;
      }

      setUploadingFileName('');
      onCancelModal();
    } catch (error) {
      message.destroy();
      setUploadingFileName('');
      let errorMessage = 'Failed to upload AI images. Please try again.';
      if (error?.graphQLErrors?.[0]?.message) {
        errorMessage = error.graphQLErrors[0].message;
      } else if (error?.networkError?.result?.errors?.[0]?.message) {
        errorMessage = error.networkError.result.errors[0].message;
      } else if (error?.message) {
        errorMessage = error.message;
      }
      message.error(errorMessage);
    }
  };

  /**
   * Check if the upload button is disabled
   * @returns {boolean} - True if the upload button is disabled, false otherwise
   */
  const isConfirmDisabled = () => {
    if (type === 'gallery' && onAddToGallery) {
      return false;
    }

    if (activeTab === 'upload') {
      const hasFiles = fileList && fileList.length > 0;
      return !hasFiles;
    } else if (activeTab === 'library') {
      const hasLibraryItems = Object.keys(selectedItems).length > 0;
      return !hasLibraryItems;
    } else if (activeTab === 'url') {
      if (!imageUrlRegex.test(urlInput) || uploadingAIImage || imageUrlError) {
        return true;
      }
      return !urlInput || !urlInput.trim();
    } else if (activeTab === 'ai' || activeTab === 'regenerate') {
      return selectedAIImages.length === 0;
    }
    return true;
  };

  /**
   * Get the text for the upload button
   * @returns {string} - The text for the upload button
   */
  const getButtonText = () => {
    if (type === 'gallery') {
      if (activeTab === 'upload') {
        return uploadStatus.status === 'uploading' ? 'UPLOADING...' : 'ADD TO GALLERY';
      } else if (activeTab === 'library') {
        return 'ADD TO GALLERY';
      } else if (activeTab === 'url') {
        return 'ADD TO GALLERY';
      } else if (activeTab === 'ai' || activeTab === 'regenerate') {
        return selectedAIImages.length > 0 ? 'ADD TO GALLERY' : 'GENERATE IMAGE';
      }
    } else if (type === 'attachments') {
      if (activeTab === 'upload') {
        return uploadStatus.status === 'uploading' ? 'UPLOADING...' : 'ATTACH';
      } else if (activeTab === 'library') {
        return 'ATTACH';
      } else if (activeTab === 'url') {
        return 'ATTACH';
      }
    }
    if (activeTab === 'upload') {
      return uploadStatus.status === 'uploading' ? 'UPLOADING...' : 'START UPLOAD';
    } else if (activeTab === 'library') {
      return 'INSERT';
    } else if (activeTab === 'url') {
      return 'ADD IMAGE';
    } else if (activeTab === 'ai' || activeTab === 'regenerate') {
      return selectedAIImages.length > 0 ? 'INSERT' : 'GENERATE IMAGE';
    }
    return 'START UPLOAD';
  };

  /**
   * Props for the uploader
   * @returns {Object} - The props for the uploader
   */
  const props = {
    multiple: true,
    onRemove: (file) => {
      const index = fileList.indexOf(file);
      const newFileList = fileList.slice();
      newFileList.splice(index, 1);
      setFileList(newFileList);
    },
    beforeUpload: (file, files) => {
      setFileList([...fileList, ...files]);
      return false;
    },
    fileList,
  };

  return (
    <>
      <Modal
        wrapClassName="gallery-modal select-box upload-image-modal"
        className="confirm-modal upload-content-library-item-modal"
        visible={showUploadModal}
        onCancel={handleCloseModal}
        width={720}
        closeIcon={<CloseIcon className="close-icon" onClick={handleCloseModal} />}
        maskClosable={true}
        onMaskClick={handleCloseModal}
        centered
        title={
          <>
            <h3 className="title">
              <span>
                {activeTab === 'regenerate'
                  ? 'Regenerate Image'
                  : type === 'covers'
                  ? `Upload ${
                      selectedCoverType === 'full-page' ? 'Full Page Cover' : 'Cover Photo'
                    }`
                  : type === 'media'
                  ? 'Media'
                  : type === 'pricing'
                  ? 'Add Thumbnail'
                  : type === 'attachments'
                  ? 'Upload Files'
                  : type === 'gallery' || type === 'image-editor'
                  ? `Select Image${type === 'gallery' ? 's' : ''}`
                  : 'Add Image'}
              </span>
            </h3>
            <Divider />
          </>
        }
        footer={[
          <div
            className={`footer-buttons-wrapper ${
              (activeTab === 'ai' || activeTab === 'regenerate') && aiImageResults.length > 0
                ? 'cancel-btn-left'
                : ''
            }`}>
            <Button key="cancel" className="large-btn grey-btn" onClick={onCancelModal}>
              CANCEL
            </Button>
            <div className="buttons-wrapper">
              {aiImageResults.length > 0 && (activeTab === 'ai' || activeTab === 'regenerate') && (
                <Button
                  key="regenerate"
                  className="large-btn grey-btn"
                  onClick={runCreateImage}
                  disabled={runningImagePrompt}>
                  REGENERATE
                </Button>
              )}
              {(!aiImageResults.length || runningImagePrompt) &&
              (activeTab === 'ai' || activeTab === 'regenerate') ? (
                <Button
                  key="generate"
                  className="large-btn secondary-btn"
                  onClick={runCreateImage}
                  loading={runningImagePrompt}
                  disabled={!aiPrompt || !aiPrompt.trim() || runningImagePrompt}
                  icon={<AIIcon />}>
                  {runningImagePrompt ? 'RUNNING' : 'GENERATE IMAGE'}
                </Button>
              ) : (
                <Button
                  key="upload"
                  className="large-btn secondary-btn"
                  onClick={handleUpload}
                  loading={
                    uploadStatus.status === 'uploading' ||
                    ((activeTab === 'ai' || activeTab === 'regenerate') &&
                      selectedAIImages.length > 0 &&
                      uploadStatus.status === 'uploading') ||
                    uploadingAIImage
                  }
                  disabled={isConfirmDisabled()}>
                  {getButtonText()}
                </Button>
              )}
            </div>
          </div>,
        ].filter(Boolean)}>
        {uploadStatus.status === 'uploading' ? (
          <div className="logo-upload-progress">
            <p className="progress-title">
              {type === 'attachments'
                ? 'Uploading File...'
                : activeTab === 'regenerate'
                ? 'Regenerating Image...'
                : `Uploading Image${fileList.length > 1 ? 's' : ''}... `}
            </p>
            <p className="upload-image-name" title={uploadingFileName}>
              {uploadingFileName}
            </p>
            <Slider
              value={uploadStatus.percent}
              strokeLinecap="round"
              status="active"
              showInfo={false}
            />
          </div>
        ) : (
          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            type="card"
            className={`upload-tabs ${activeTab === 'library' ? 'library-tab-content' : ''}`}>
            {activeTab !== 'regenerate' && (
              <TabPane tab="Upload" key="upload">
                <div className="select-image">
                  <Dragger
                    accept={
                      type === 'attachments'
                        ? '*' // Accept all file types for attachments
                        : 'image/x-png,image/*,image/png,image/bmp,image/jpeg,image/svg+xml,.jpg, .jpeg, .png' +
                          (type === 'media' ? ', .pdf, .doc, .docx, .gif, .xls, .xlsx' : '')
                    }
                    {...props}>
                    <Col className="select-image-uploader">
                      <UploadCloudIcon />
                      <p className="select-image-title">
                        {fileList && fileList.length > 0
                          ? `Total number of files ${fileList.length}`
                          : type === 'attachments'
                          ? `Start by adding your files`
                          : `Drag & drop your file`}
                      </p>
                      <p className="select-image-info">or upload from your computer</p>
                      <p className="select-image-details">
                        Maximum {type === 'covers' ? 1.5 : type === 'attachments' ? 3 : 3}MB
                      </p>
                      {type === 'covers' ? (
                        <span className="image-size">
                          Recommended image sizes - Small: 465px height x 900px width <br />
                          Large: 1250px height x 900px width
                        </span>
                      ) : type === 'pricing' ? (
                        <span className="image-size">
                          Recommended image size - 40px height x 40px width
                        </span>
                      ) : (
                        <span className="image-size">
                          Recommended image size - 465px height x 900px width
                        </span>
                      )}
                      <Row className="select-image-upload-text">
                        <Button
                          className="large-btn transparent-btn"
                          icon={<ArrowTop className="select-image-upload-icon" />}>
                          {type === 'attachments' ? 'UPLOAD FILES' : 'UPLOAD IMAGE'}
                        </Button>
                      </Row>
                    </Col>
                  </Dragger>
                  {uploadedImagesUrls.length > 0 && (
                    <div className="image-preview">
                      {uploadedImagesUrls.map((img) => (
                        <img src={img} key={img} alt="" />
                      ))}
                    </div>
                  )}
                </div>
              </TabPane>
            )}

            {!isWix && type !== 'media' && activeTab !== 'regenerate' && (
              <TabPane tab="Library" key="library">
                <ContentLibrary
                  selectedItems={selectedItems}
                  setSeletedItem={(items) => {
                    const itemKeys = Object.keys(items);
                    if (
                      itemKeys.length > 1 &&
                      type !== 'pricing' &&
                      type !== 'catalogs' &&
                      !multiSelect
                    ) {
                      const lastKey = itemKeys[itemKeys.length - 1];
                      setSeletedItem({ [lastKey]: items[lastKey] });
                    } else {
                      setSeletedItem(items);
                    }
                  }}
                  showSelectedFiles={true}
                  showBack={showBack}
                  setShowBack={setShowBack}
                  span={8}
                  type={type}
                  multiSelect={multiSelect}
                />
              </TabPane>
            )}

            {activeTab !== 'regenerate' && (
              <TabPane tab="URL" key="url">
                <div className="url-tab-content">
                  <Form form={urlForm} layout="vertical">
                    <Form.Item
                      rules={[
                        {
                          required: true,
                          message: 'Image URL is required.',
                        },
                        {
                          type: 'url',
                          warningOnly: true,
                        },
                        {
                          pattern: imageUrlRegex,
                          message: 'Please enter a valid image URL.',
                        },
                        {
                          type: 'string',
                          max: 2048,
                          message: 'URL must be less than 2048 characters long.',
                        },
                        {
                          type: 'string',
                          pattern: imageUrlRegex,
                          message: 'Please enter a valid URL.',
                        },
                      ]}>
                      <Input
                        type="url"
                        pattern="https://.*"
                        placeholder="Paste image URL (https://...)"
                        value={urlInput}
                        onChange={(e) => {
                          setUrlInput(e.target.value);
                          if (!imageUrlRegex.test(e.target.value)) {
                            setImageUrlError(true);
                          } else {
                            setImageUrlError(false);
                          }
                        }}
                        size="large"
                      />
                    </Form.Item>
                  </Form>

                  {imageUrlRegex.test(urlInput) && !imageUrlError && (
                    <div className="url-image-preview">
                      <img
                        src={urlInput}
                        alt="URL preview"
                        onError={(e) => setImageUrlError(true)}
                      />
                    </div>
                  )}
                  {(!imageUrlRegex.test(urlInput) || imageUrlError) && (
                    <div className="url-info-area">
                      <div className="url-placeholder-icon">
                        <DefaultImageIcon />
                      </div>
                      <p className="url-info-text">Maximum 3MB</p>
                      <p className="url-info-details">
                        Recommended image size - 465px height x 900px width
                      </p>
                    </div>
                  )}
                </div>
              </TabPane>
            )}

            {type !== 'attachments' && (
              <TabPane
                tab="Create with AI"
                key={`${activeTab === 'regenerate' ? 'regenerate' : 'ai'}`}>
                <div className="ai-tab-content">
                  <Form form={aiForm} layout="vertical">
                    <Form.Item label="Write your prompt">
                      <TextArea
                        rows={6}
                        placeholder="A cool, wide shot of the wild west"
                        value={aiPrompt}
                        onChange={(e) => setAiPrompt(e.target.value)}
                        maxLength={1000}
                        disabled={runningImagePrompt}
                      />
                    </Form.Item>
                  </Form>
                  <div className="ai-image-results">
                    <Row gutter={[15, 24]}>
                      {aiImageResults.map((item, itemIndex) => {
                        const imageUrl = normalizeAIImageUrl(
                          typeof item === 'string' ? item : item.url
                        );
                        const isSelected = selectedAIImages.some(
                          (img) => normalizeAIImageUrl(img.url) === imageUrl
                        );

                        return (
                          <Col span={24} key={itemIndex} className="ai-image-item">
                            <Card
                              className={`image-card ${isSelected ? 'selected' : ''}`}
                              bordered={false}
                              cover={<img className="ai-image-preview" src={imageUrl} alt="" />}
                              onClick={() => {
                                if (isSelected) {
                                  handleAIImageRemove(item);
                                } else {
                                  handleAIImageSelection(item);
                                }
                              }}
                            />
                          </Col>
                        );
                      })}
                    </Row>
                  </div>
                </div>
              </TabPane>
            )}
          </Tabs>
        )}
      </Modal>

      {showCloseConfirm && (
        <Popconfirm
          visible={showCloseConfirm}
          title={'Do you want to exit?'}
          closable={false}
          cancelText={'CONTINUE'}
          confirmText={'YES, EXIT'}
          onCancel={handleCancelClose}
          onConfirm={handleConfirmClose}
          footer={null}
        />
      )}
    </>
  );
};

/**
 * Default props
 */
UploadContentLibraryItem.defaultProps = {
  selectedCoverType: 'full-page',
  showUploadModal: false,
  isWix: false,
  multiSelect: false,
  prop: {},
  onSuccess: () => {},
  type: 'upload',
  selectedFolderKey: null,
};

/**
 * Props
 */
UploadContentLibraryItem.propTypes = {
  selectedCoverType: PropTypes.string,
  cancelUploadModal: PropTypes.func.isRequired,
  showUploadModal: PropTypes.bool,
  isWix: PropTypes.bool,
  onSuccess: PropTypes.func,
  selectedFolderKey: PropTypes.string,
  type: PropTypes.string.isRequired,
  multiSelect: PropTypes.bool,
  prop: PropTypes.object,
};

export default UploadContentLibraryItem;
